import React, { type FC } from 'react'
import { Box, outlinedInputClasses, type SxProps, type Theme, Typography } from '@mui/material'

import { formatDate, formatTime, isSameCalendarDay, type ReadonlyDate } from '../../../../utils'

import { Translation } from '../../../../domain'

import { useTranslator } from '../../Internationalization'
import { extractDataTestId } from '../../utils'
import { TextField } from '../Input'

export type TimeInputsProps = Readonly<{
    startDate?: ReadonlyDate
    endDate?: ReadonlyDate
    startTime: string
    endTime: string
    /** The earliest time selectable on the end field, e.g. the start time when start and end fall on the same day. */
    endMinTime?: string
    /** The current date/time, shared by the caller so every "is this today" / max-time check agrees on the same instant. */
    now: ReadonlyDate
    onStartTimeChange: (time: string) => void
    onEndTimeChange: (time: string) => void
}>

const rowStyle: SxProps<Theme> = {
    display: 'flex',
    alignItems: 'center',
    gap: 1.5,
}

const containerStyle: SxProps<Theme> = {
    display: 'flex',
    flexDirection: 'column',
    gap: 1.5,
}

const labelStyle: SxProps<Theme> = (theme: Theme) => ({
    ...theme.typography.body2,
    minWidth: 40,
    fontWeight: 600,
    color: 'grey.700',
})

const dateInputStyle: SxProps<Theme> = {
    minWidth: 160,
}

const timeInputStyle: SxProps<Theme> = {
    minWidth: 80,
    [`& .${outlinedInputClasses.input}[type="time"]::-webkit-calendar-picker-indicator`]: {
        display: 'none',
    },
}

type TimeInputProps = Readonly<{
    label: Translation
    date?: ReadonlyDate
    time: string
    minTime?: string
    now: ReadonlyDate
    onTimeChange: (time: string) => void
    datePlaceholderId: Translation
}>

const TimeInput: FC<TimeInputProps> = ({ label, date, time, minTime, now, onTimeChange, datePlaceholderId, ...props }) => {
    const translator = useTranslator()

    // Logs aren't available for future times, so today's selection can't go past the current time
    const maxTime = date && isSameCalendarDay(date, now) ? formatTime(now) : undefined

    return (
        <Box sx={rowStyle}>
            <Typography sx={labelStyle}>{translator.formatTranslation(label)}</Typography>

            <TextField
                type="text"
                value={date ? formatDate(date) : ''}
                sx={dateInputStyle}
                data-testid={extractDataTestId(props, '-date-input')}
                slotProps={{ input: { readOnly: true, sx: { typography: 'body2' } } }}
                placeholder={translator.formatTranslation(datePlaceholderId)}
            />

            <TextField
                type="time"
                value={date ? time : ''}
                disabled={!date}
                onChange={(e) => onTimeChange(e.target.value)}
                sx={timeInputStyle}
                slotProps={{ htmlInput: { max: maxTime, min: minTime } }}
                data-testid={extractDataTestId(props, '-time-input')}
                placeholder={translator.formatTranslation(Translation.TIME)}
            />
        </Box>
    )
}

export const TimeInputs: FC<TimeInputsProps> = ({ startTime, endTime, startDate, endDate, endMinTime, now, onStartTimeChange, onEndTimeChange }) => (
    <Box sx={containerStyle}>
        <TimeInput
            label={Translation.START}
            date={startDate}
            time={startTime}
            now={now}
            onTimeChange={onStartTimeChange}
            data-testid="start"
            datePlaceholderId={Translation.START_DATE}
        />

        <TimeInput
            label={Translation.END}
            date={endDate}
            time={endTime}
            minTime={endMinTime}
            now={now}
            onTimeChange={onEndTimeChange}
            data-testid="end"
            datePlaceholderId={Translation.END_DATE}
        />
    </Box>
)
